/**
 * Shared photo upload/compression configuration and logic.
 *
 * The actual image resizing is platform-specific:
 *  - Web uses OffscreenCanvas + createImageBitmap
 *  - Mobile uses expo-image-manipulator
 *
 * This module extracts the duplicated pure logic:
 *  - Rendition tier specifications (dimensions, quality)
 *  - Upload orchestration types
 *  - Validation helpers (maxBytes enforcement)
 */
/**
 * Rendition specs for the free tier.
 * Only display + thumbnail are produced (no archival original).
 */
export const FREE_DISPLAY = { maxEdge: 1024, quality: 0.5 };
/**
 * Rendition specs for the paid tier display image.
 * Higher resolution and quality than free.
 */
export const PAID_DISPLAY = { maxEdge: 1600, quality: 0.7 };
/**
 * Rendition specs for the paid tier archival original.
 * Maximum resolution, high quality.
 */
export const PAID_ORIGINAL = { maxEdge: 4096, quality: 0.9 };
/** Thumbnail rendition spec — shared across all tiers. */
export const THUMB = { maxEdge: 320, quality: 0.6 };
// --- Validation helpers ---
/**
 * Validate that a payload does not exceed the server-specified maxBytes limit.
 * Throws a descriptive error if the limit is exceeded.
 *
 * @param size - Actual size in bytes of the payload to upload.
 * @param maxBytes - Optional server-imposed limit from the presign response.
 * @throws Error if size exceeds maxBytes.
 */
export function validateUploadSize(size, maxBytes) {
    if (maxBytes != null && size > maxBytes) {
        throw new Error(`File size (${size} bytes) exceeds the maximum allowed (${maxBytes} bytes)`);
    }
}
/**
 * Determine which rendition specs to produce based on config.
 *
 * @param config - Compression/tier configuration.
 * @returns Object mapping rendition names to their specs.
 */
export function getRenditionSpecs(config) {
    const storeOriginal = config?.storeOriginal ?? false;
    if (storeOriginal) {
        return {
            display: PAID_DISPLAY,
            thumb: THUMB,
            original: PAID_ORIGINAL,
        };
    }
    return {
        display: FREE_DISPLAY,
        thumb: THUMB,
    };
}
//# sourceMappingURL=photo.js.map